/**
 * Default ("Diolo standard" — men's) size charts. Used as the
 * SizeGuideModal fallback when a product has no per-product
 * `sizeChartData` set, and also as the pre-seed for the admin "edit
 * size chart" form so a merchant editing a product starts from this
 * matrix instead of a blank table.
 *
 * Single source of truth — both the storefront component
 * (app/components/storefront/SizeGuideModal.tsx) and the admin form
 * (app/routes/admin.products.$id.tsx) import from here. Editing
 * either set propagates to both places at the next deploy.
 *
 * Numbers are men's body measurements in cm (the storefront has its own
 * cm↔in toggle). These are generic menswear fallbacks; once the real
 * catalog ships, per-product `sizeChartData` overrides them.
 */

export type Tab = "tops" | "bottoms" | "footwear";

export const SIZES_XS_XXL: string[] = ["XS", "S", "M", "L", "XL", "XXL"];

export const TOPS_DATA: Record<string, number[]> = {
  chest:    [90, 97, 104, 111, 118, 125],
  length:   [66, 68, 70, 72, 74, 76],
  shoulder: [42, 44, 46, 48, 50, 52],
  sleeve:   [21, 22, 23, 24, 25, 26],
};

export const BOTTOMS_DATA: Record<string, number[]> = {
  waist:  [70, 76, 82, 88, 94, 100],
  hip:    [90, 96, 102, 108, 114, 120],
  length: [42, 44, 46, 48, 50, 52],
};

export const FOOTWEAR_SIZES: string[] = ["36", "37", "38", "39", "40", "41", "42", "43", "44"];
export const FOOTWEAR_DATA: Record<string, number[]> = {
  footLength: [22.5, 23.0, 23.5, 24.0, 24.5, 25.0, 25.5, 26.0, 26.5],
  eu:         [36, 37, 38, 39, 40, 41, 42, 43, 44],
  us:         [5, 5.5, 6, 6.5, 7, 8, 8.5, 9, 9.5],
};

/**
 * Pick the appropriate default chart category from a product's
 * `productType` string. Returns "tops" when nothing matches.
 *
 * The same mapping was previously inlined in SizeGuideModal as
 * `detectTab(productType)`. Lifted here so the admin pre-seed
 * uses the identical heuristic.
 */
export function detectChartTab(productType?: string | null): Tab {
  if (!productType) return "tops";
  const lower = productType.toLowerCase();
  if (/pant|short|skirt|legging|jogger|bottom|quần/i.test(lower)) return "bottoms";
  if (/shoe|sneaker|sandal|boot|footwear|giày|dép/i.test(lower)) return "footwear";
  return "tops";
}

/**
 * Get the default chart bundle (sizes + measurements) for a category.
 */
export function getDefaultChart(tab: Tab): {
  sizes: string[];
  measurements: Record<string, number[]>;
} {
  if (tab === "footwear") {
    return { sizes: FOOTWEAR_SIZES, measurements: FOOTWEAR_DATA };
  }
  if (tab === "bottoms") {
    return { sizes: SIZES_XS_XXL, measurements: BOTTOMS_DATA };
  }
  return { sizes: SIZES_XS_XXL, measurements: TOPS_DATA };
}

/**
 * Shape of a per-product size chart override. Persisted as JSON in
 * Product.sizeChartData. Validated server-side on write — the
 * storefront read path tolerates malformed values by falling back to
 * the default chart for the matching tab.
 */
export interface SizeChartData {
  category: Tab;
  sizes: string[];
  measurements: Record<string, number[]>;
}

/**
 * Strict-ish validator: returns the parsed value if it's a usable
 * SizeChartData, or null if not. Logs nothing — callers decide
 * whether absence of a chart deserves a warning. The storefront
 * uses a defensive `Math.min(sizes.length, values.length)` cap as
 * second-line defence in case a row escapes validation somehow.
 */
export function parseSizeChartData(value: unknown): SizeChartData | null {
  if (!value || typeof value !== "object") return null;
  const v = value as Record<string, unknown>;
  if (v.category !== "tops" && v.category !== "bottoms" && v.category !== "footwear") {
    return null;
  }
  if (!Array.isArray(v.sizes) || v.sizes.length < 2) return null;
  if (!v.sizes.every((s) => typeof s === "string" && s.length > 0)) return null;
  if (!v.measurements || typeof v.measurements !== "object") return null;
  const measurements: Record<string, number[]> = {};
  const sizesLen = v.sizes.length;
  for (const [key, arr] of Object.entries(v.measurements as Record<string, unknown>)) {
    if (!Array.isArray(arr)) continue;
    if (arr.length !== sizesLen) continue;
    if (!arr.every((n) => typeof n === "number" && Number.isFinite(n) && n > 0 && n < 500)) continue;
    measurements[key] = arr as number[];
  }
  if (Object.keys(measurements).length === 0) return null;
  return {
    category: v.category as Tab,
    sizes: v.sizes as string[],
    measurements,
  };
}
